"use client";

import { useState, useEffect, useRef, useCallback, type FormEvent } from "react";
import { useLocale } from "next-intl";
import { motion, AnimatePresence } from "framer-motion";
import { FiMessageCircle, FiX, FiRefreshCw, FiSend, FiUser } from "react-icons/fi";
import ReactMarkdown from "react-markdown";
import TrekksoftBookie, { type BookieOption } from "@/components/transfers/TrekksoftBookie";

// Map of activity ID → Bookie option metadata, for inline booking widgets
// the AI can embed via {{booking:NNNN}} markers in its replies.
const BOOKING_OPTIONS: Record<number, BookieOption> = {
  323977: { id: 323977, label: "Shared Roundtrip", sublabel: "from $30 per guest · Corendon Exclusive" },
  324053: { id: 324053, label: "Private Transfer", sublabel: "from $85 per vehicle" },
};

const BOOKING_MARKER = /\{\{booking:(\d+)\}\}/g;

type MessagePart =
  | { type: "text"; content: string }
  | { type: "booking"; activityId: number };

function parseMessageParts(content: string): MessagePart[] {
  const parts: MessagePart[] = [];
  let lastIdx = 0;
  let match: RegExpExecArray | null;
  BOOKING_MARKER.lastIndex = 0;
  while ((match = BOOKING_MARKER.exec(content)) !== null) {
    if (match.index > lastIdx) {
      parts.push({ type: "text", content: content.slice(lastIdx, match.index) });
    }
    parts.push({ type: "booking", activityId: parseInt(match[1], 10) });
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < content.length) {
    parts.push({ type: "text", content: content.slice(lastIdx) });
  }
  return parts.length ? parts : [{ type: "text", content }];
}

type Message = {
  role: "user" | "model";
  content: string;
  timestamp: string;
};

const QUICK_REPLIES: Record<string, string[]> = {
  en: [
    "Tell me about FBTT Travel",
    "What tours are available?",
    "I need an airport transfer",
    "Best beaches in Curaçao?",
  ],
  es: [
    "Cuéntame sobre FBTT Travel",
    "¿Qué tours hay disponibles?",
    "Necesito un transfer al aeropuerto",
    "¿Mejores playas de Curazao?",
  ],
  de: [
    "Erzähl mir über FBTT Travel",
    "Welche Touren gibt es?",
    "Ich brauche einen Flughafentransfer",
    "Beste Strände auf Curaçao?",
  ],
  nl: [
    "Vertel me over FBTT Travel",
    "Welke tours zijn er beschikbaar?",
    "Ik heb een luchthaventransfer nodig",
    "Beste stranden van Curaçao?",
  ],
  pap: [
    "Kontami tokante FBTT Travel",
    "Ki tournan tin disponibel?",
    "Mi mester un transfer di aeropuerto",
    "Mihó playanan di Kòrsou?",
  ],
};

const WELCOME_FEATURES: Record<string, string[]> = {
  en: [
    "Plan your Curaçao trip",
    "Book airport transfers",
    "Explore tours and excursions",
    "Get local tips and advice",
  ],
  es: [
    "Planifica tu viaje a Curazao",
    "Reserva transfers al aeropuerto",
    "Explora tours y excursiones",
    "Obtén consejos locales",
  ],
  de: [
    "Planen Sie Ihre Curaçao-Reise",
    "Flughafentransfers buchen",
    "Touren und Ausflüge entdecken",
    "Lokale Tipps erhalten",
  ],
  nl: [
    "Plan je Curaçao-reis",
    "Boek luchthaventransfers",
    "Ontdek tours en excursies",
    "Krijg lokale tips en advies",
  ],
  pap: [
    "Planeá bo biahe pa Kòrsou",
    "Reservá transfer di aeropuerto",
    "Eksploré tournan i excursionnan",
    "Haña consehonan lokal",
  ],
};

const UI_STRINGS: Record<string, { greeting: string; subtitle: string; welcomeMsg: string; placeholder: string; fabLabel: string; errorMsg: string }> = {
  en: {
    greeting: "Bon Bini!",
    subtitle: "Your Curaçao Travel Assistant",
    welcomeMsg: "Welcome to FBTT Travel. I'm your AI assistant — ask me anything about Curaçao!",
    placeholder: "Ask about Curaçao...",
    fabLabel: "Ask about Curaçao",
    errorMsg: "Sorry, I couldn't connect right now. Please try again or contact us at info@fb-tt.com.",
  },
  es: {
    greeting: "¡Bon Bini!",
    subtitle: "Tu Asistente de Viaje en Curazao",
    welcomeMsg: "Bienvenido a FBTT Travel. Soy tu asistente de IA — ¡pregúntame lo que quieras sobre Curazao!",
    placeholder: "Pregunta sobre Curazao...",
    fabLabel: "Pregunta sobre Curazao",
    errorMsg: "Lo siento, no pude conectar ahora. Inténtalo de nuevo o contáctanos en info@fb-tt.com.",
  },
  de: {
    greeting: "Bon Bini!",
    subtitle: "Ihr Curaçao-Reiseassistent",
    welcomeMsg: "Willkommen bei FBTT Travel. Ich bin Ihr KI-Assistent — fragen Sie mich alles über Curaçao!",
    placeholder: "Fragen Sie über Curaçao...",
    fabLabel: "Fragen zu Curaçao",
    errorMsg: "Entschuldigung, ich konnte gerade keine Verbindung herstellen. Bitte versuchen Sie es erneut oder kontaktieren Sie uns unter info@fb-tt.com.",
  },
  nl: {
    greeting: "Bon Bini!",
    subtitle: "Uw Curaçao Reisassistent",
    welcomeMsg: "Welkom bij FBTT Travel. Ik ben uw AI-assistent — stel me al uw vragen over Curaçao!",
    placeholder: "Vraag over Curaçao...",
    fabLabel: "Vraag over Curaçao",
    errorMsg: "Sorry, ik kon nu geen verbinding maken. Probeer het opnieuw of neem contact met ons op via info@fb-tt.com.",
  },
  pap: {
    greeting: "Bon Bini!",
    subtitle: "Bo Asistensia di Biahe pa Kòrsou",
    welcomeMsg: "Bon bini na FBTT Travel. Mi ta bo asistensia di AI — puntra mi loke bo ke tokante Kòrsou!",
    placeholder: "Puntra tokante Kòrsou...",
    fabLabel: "Puntra tokante Kòrsou",
    errorMsg: "Despensa, mi no por a konektá awor. Purba atrobe of tuma kontakto ku nos na info@fb-tt.com.",
  },
};

export default function ChatDrawer() {
  const locale = useLocale();
  const strings = UI_STRINGS[locale] || UI_STRINGS.en;
  const quickReplies = QUICK_REPLIES[locale] || QUICK_REPLIES.en;
  const welcomeFeatures = WELCOME_FEATURES[locale] || WELCOME_FEATURES.en;

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  // Lock body scroll when open on mobile
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Load from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem("fbtt_chat");
      if (saved) setMessages(JSON.parse(saved));
    } catch {}
  }, []);

  // Save to localStorage
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem("fbtt_chat", JSON.stringify(messages.slice(-50)));
    }
  }, [messages]);

  // Auto-scroll
  useEffect(() => {
    if (chatRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatRef.current;
      const nearBottom = scrollHeight - scrollTop - clientHeight < 150;
      if (nearBottom || streamingContent || isStreaming) {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [messages, streamingContent, isStreaming]);

  const handleScroll = useCallback(() => {
    if (chatRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatRef.current;
      setShowScrollBtn(scrollHeight - scrollTop - clientHeight > 100);
    }
  }, []);

  const scrollToBottom = () => endRef.current?.scrollIntoView({ behavior: "smooth" });

  const sendMessage = async (text: string) => {
    if (!text.trim() || isStreaming) return;

    const userMsg: Message = { role: "user", content: text, timestamp: new Date().toISOString() };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput("");
    setIsStreaming(true);
    setStreamingContent("");

    // Build history for API (last 20 messages)
    const history = updatedMessages.slice(-20).map((m) => ({
      role: m.role,
      parts: [{ text: m.content }],
    }));
    // Remove the last user message since API adds it
    history.pop();

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, history, locale }),
      });

      if (!res.ok) throw new Error("Failed");

      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let accumulated = "";

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split("\n");

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.type === "content") {
                  accumulated += data.content;
                  setStreamingContent(accumulated);
                } else if (data.type === "done") {
                  setMessages((prev) => [
                    ...prev,
                    { role: "model", content: accumulated, timestamp: new Date().toISOString() },
                  ]);
                  setStreamingContent("");
                }
              } catch {}
            }
          }
        }
      }

      // If stream ended without "done" event
      if (accumulated && streamingContent) {
        setMessages((prev) => [
          ...prev,
          { role: "model", content: accumulated, timestamp: new Date().toISOString() },
        ]);
        setStreamingContent("");
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "model", content: strings.errorMsg, timestamp: new Date().toISOString() },
      ]);
      setStreamingContent("");
    }

    setIsStreaming(false);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleReset = () => {
    setMessages([]);
    setStreamingContent("");
    localStorage.removeItem("fbtt_chat");
  };

  const formatTime = (ts: string) => {
    try {
      return new Date(ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch { return ""; }
  };

  const hasStarted = messages.length > 0;

  // Custom markdown renderer — links open in same window (no target="_blank")
  const markdownComponents = {
    a: ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
      <a
        href={href}
        {...props}
        className="font-medium text-fbtt-orange underline decoration-fbtt-orange/30 underline-offset-2 transition-colors hover:text-fbtt-orange/80 hover:decoration-fbtt-orange/60"
        onClick={() => setIsOpen(false)}
      >
        {children}
      </a>
    ),
    p: ({ children, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
      <p {...props} className="mb-2 last:mb-0">{children}</p>
    ),
    ul: ({ children, ...props }: React.HTMLAttributes<HTMLUListElement>) => (
      <ul {...props} className="mb-2 ml-4 list-disc space-y-1 last:mb-0">{children}</ul>
    ),
    li: ({ children, ...props }: React.HTMLAttributes<HTMLLIElement>) => (
      <li {...props} className="text-[15px]">{children}</li>
    ),
    strong: ({ children, ...props }: React.HTMLAttributes<HTMLElement>) => (
      <strong {...props} className="font-semibold">{children}</strong>
    ),
  };

  return (
    <>
      {/* Floating Action Button — raised on mobile to clear MobileActionBar */}
      <div className="fixed bottom-24 right-4 z-50 lg:bottom-6 lg:right-6">
        <AnimatePresence>
          {!isOpen && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
            >
              <motion.button
                className="flex items-center gap-2 rounded-full bg-fbtt-orange px-4 py-3 font-semibold text-white shadow-lg transition-shadow hover:shadow-xl md:px-5"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setIsOpen(true)}
              >
                <FiMessageCircle className="h-5 w-5" />
                <span className="hidden sm:inline">{strings.fabLabel}</span>
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Drawer */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 bg-black/30"
              onClick={() => setIsOpen(false)}
            />

            {/* Panel */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed bottom-0 right-0 top-0 z-50 flex w-full flex-col bg-white shadow-2xl sm:w-[480px] md:w-[520px] lg:w-[560px]"
            >
              {/* Header */}
              <div className="flex flex-shrink-0 items-center justify-between bg-fbtt-navy px-4 py-4 pt-[max(1rem,env(safe-area-inset-top))]">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-fbtt-orange shadow-lg">
                    <FiMessageCircle className="text-xl text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold tracking-tight text-white">FBTT Travel</h3>
                    {isStreaming ? (
                      <div className="flex items-center gap-1 text-xs text-white/80">
                        <span>Typing</span>
                        <span className="flex gap-0.5">
                          <span className="h-1 w-1 animate-bounce rounded-full bg-white/80" style={{ animationDelay: "0ms" }} />
                          <span className="h-1 w-1 animate-bounce rounded-full bg-white/80" style={{ animationDelay: "150ms" }} />
                          <span className="h-1 w-1 animate-bounce rounded-full bg-white/80" style={{ animationDelay: "300ms" }} />
                        </span>
                      </div>
                    ) : (
                      <p className="text-xs text-white/60">{strings.subtitle}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={handleReset}
                    className="rounded-lg p-2 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
                    title="Reset chat"
                  >
                    <FiRefreshCw className="h-[18px] w-[18px]" />
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="rounded-lg p-2 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
                  >
                    <FiX className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex flex-1 flex-col overflow-hidden bg-gray-50">
                {!hasStarted ? (
                  /* Welcome Screen */
                  <div className="flex flex-1 flex-col overflow-y-auto p-6">
                    <div className="mx-auto flex max-w-sm flex-1 flex-col items-center justify-center text-center">
                      <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-fbtt-orange shadow-lg shadow-fbtt-orange/25">
                        <FiMessageCircle className="text-4xl text-white" />
                      </div>
                      <h2 className="mb-2 text-2xl font-bold text-gray-900">{strings.greeting}</h2>
                      <p className="mb-8 text-gray-600">{strings.welcomeMsg}</p>

                      <div className="w-full space-y-3 text-left">
                        {welcomeFeatures.map((item, i) => (
                          <div key={i} className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
                            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-fbtt-orange/10">
                              <svg className="h-4 w-4 text-fbtt-orange" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <span className="text-sm text-gray-700">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Quick start buttons */}
                    <div className="mt-6 grid grid-cols-2 gap-2">
                      {quickReplies.map((reply) => (
                        <button
                          key={reply}
                          onClick={() => sendMessage(reply)}
                          className="rounded-xl border border-fbtt-orange/20 bg-white px-3 py-2.5 text-left text-sm font-medium text-fbtt-navy shadow-sm transition-all hover:border-fbtt-orange hover:bg-fbtt-orange hover:text-white hover:shadow-md"
                        >
                          {reply}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  /* Chat Messages */
                  <>
                    <div
                      ref={chatRef}
                      className="flex-1 space-y-4 overflow-y-auto p-4"
                      onScroll={handleScroll}
                    >
                      {messages.map((msg, i) => (
                        <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                          <div className={`flex items-end gap-2 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"} max-w-[85%]`}>
                            {msg.role === "model" && (
                              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-fbtt-orange shadow-sm">
                                <FiMessageCircle className="text-sm text-white" />
                              </div>
                            )}
                            {msg.role === "user" && (
                              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-200">
                                <FiUser className="text-sm text-gray-600" />
                              </div>
                            )}
                            <div
                              className={`rounded-2xl px-4 py-3 shadow-sm ${
                                msg.role === "user"
                                  ? "rounded-br-md bg-fbtt-navy text-white"
                                  : "rounded-bl-md border border-gray-100 bg-white text-gray-800"
                              }`}
                            >
                              <div className="text-[15px] leading-relaxed">
                                {msg.role === "model" ? (
                                  parseMessageParts(msg.content).map((part, idx) =>
                                    part.type === "text" ? (
                                      <ReactMarkdown key={idx} components={markdownComponents}>
                                        {part.content}
                                      </ReactMarkdown>
                                    ) : BOOKING_OPTIONS[part.activityId] ? (
                                      <div key={idx} className="-mx-2 my-3 sm:-mx-3">
                                        <TrekksoftBookie
                                          options={[BOOKING_OPTIONS[part.activityId]]}
                                          height={620}
                                        />
                                      </div>
                                    ) : null
                                  )
                                ) : (
                                  msg.content
                                )}
                              </div>
                              <div className={`mt-1.5 text-right text-[10px] ${msg.role === "user" ? "text-white/50" : "text-gray-400"}`}>
                                {formatTime(msg.timestamp)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Streaming message */}
                      {streamingContent && (
                        <div className="flex justify-start">
                          <div className="flex max-w-[85%] items-end gap-2">
                            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-fbtt-orange shadow-sm">
                              <FiMessageCircle className="text-sm text-white" />
                            </div>
                            <div className="rounded-2xl rounded-bl-md border border-gray-100 bg-white px-4 py-3 text-gray-800 shadow-sm">
                              <div className="text-[15px] leading-relaxed">
                                {parseMessageParts(streamingContent).map((part, idx) =>
                                  part.type === "text" ? (
                                    <ReactMarkdown key={idx} components={markdownComponents}>
                                      {part.content}
                                    </ReactMarkdown>
                                  ) : BOOKING_OPTIONS[part.activityId] ? (
                                    <div key={idx} className="-mx-2 my-3 sm:-mx-3">
                                      <TrekksoftBookie
                                        options={[BOOKING_OPTIONS[part.activityId]]}
                                        height={620}
                                      />
                                    </div>
                                  ) : null
                                )}
                              </div>
                              <div className="mt-2 flex items-center gap-1">
                                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-fbtt-orange" />
                                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-fbtt-sky" style={{ animationDelay: "150ms" }} />
                                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-fbtt-golden" style={{ animationDelay: "300ms" }} />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Typing indicator (no content yet) */}
                      {isStreaming && !streamingContent && (
                        <div className="flex justify-start">
                          <div className="flex max-w-[85%] items-end gap-2">
                            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-fbtt-orange shadow-sm">
                              <FiMessageCircle className="text-sm text-white" />
                            </div>
                            <div className="rounded-2xl rounded-bl-md border border-gray-100 bg-white px-4 py-3 shadow-sm">
                              <div className="flex items-center gap-1.5">
                                <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400" style={{ animationDelay: "0ms" }} />
                                <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400" style={{ animationDelay: "150ms" }} />
                                <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400" style={{ animationDelay: "300ms" }} />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div ref={endRef} />
                    </div>

                    {/* Scroll to bottom */}
                    {showScrollBtn && (
                      <button
                        onClick={scrollToBottom}
                        className="absolute bottom-32 right-6 rounded-full border border-gray-200 bg-white p-2 shadow-lg transition-colors hover:bg-gray-50"
                      >
                        <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      </button>
                    )}

                    {/* Quick replies */}
                    {messages.length > 0 && !isStreaming && (
                      <div className="border-t border-gray-100 bg-gray-50 px-4 py-2">
                        <div className="scrollbar-hide flex gap-2 overflow-x-auto pb-1">
                          {quickReplies.map((reply) => (
                            <button
                              key={reply}
                              onClick={() => sendMessage(reply)}
                              className="flex-shrink-0 whitespace-nowrap rounded-full border border-fbtt-orange/20 bg-white px-4 py-1.5 text-xs font-medium text-fbtt-navy shadow-sm transition-all hover:border-fbtt-orange hover:bg-fbtt-orange hover:text-white"
                            >
                              {reply}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Input */}
                    <form
                      onSubmit={handleSubmit}
                      className="flex-shrink-0 border-t border-gray-200 bg-white px-4 pb-1 pt-3"
                    >
                      <div className="flex items-center gap-3">
                        <input
                          ref={inputRef}
                          className="flex-1 rounded-xl border-0 bg-gray-100 px-4 py-3 text-base text-gray-900 placeholder-gray-500 transition-all focus:outline-none focus:ring-2 focus:ring-fbtt-orange"
                          type="text"
                          placeholder={strings.placeholder}
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          disabled={isStreaming}
                          autoComplete="off"
                        />
                        <button
                          type="submit"
                          className={`flex items-center justify-center rounded-xl bg-fbtt-orange p-3 text-white shadow-md transition-all hover:shadow-lg active:scale-95 ${
                            isStreaming || !input.trim() ? "cursor-not-allowed opacity-60" : ""
                          }`}
                          disabled={isStreaming || !input.trim()}
                        >
                          {isStreaming ? (
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          ) : (
                            <FiSend className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {/* AI Makers credit */}
                      <div className="pb-[max(0.25rem,env(safe-area-inset-bottom))] pt-2 text-center">
                        <a
                          href="https://www.aimakers.co"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] text-gray-400 transition-colors hover:text-gray-600"
                        >
                          Chatbot by aimakers.co
                        </a>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
